import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Heart, Target, Lock, ArrowRight, Check, X, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { CounterAnimation } from "../components/CounterAnimation";
import { DualSplitVideoBackground } from "../components/VideoBackground";

// Video URLs for dual-split hero
// Left: Working mom, busy but ready for comeback
// Center: Late 20s early career, dreaming of something new (male)
const LEFT_VIDEO = "https://assets.mixkit.co/videos/52255/52255-720.mp4"; // Young mother working on laptop
const CENTER_VIDEO = "https://assets.mixkit.co/videos/25251/25251-720.mp4"; // Worried man by train window, contemplating

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

// Expanded content for principles
const principleDetails = {
  "01": {
    name: "Human Agency",
    expanded: "You are never a passenger on this journey. Become gives you the tools, the insights, and the support. But every decision is yours. We build your confidence to lead your own life, not follow ours."
  },
  "02": {
    name: "Truth Over Comfort",
    expanded: "Growth doesn't come from hearing what you want to hear. Become offers honest reflection: gentle, but real. Because lasting change requires seeing yourself clearly, not just feeling good in the moment."
  },
  "03": {
    name: "Privacy as Safety",
    expanded: "You can't be honest if you're worried about who's watching. Become's architecture was built by a cybersecurity engineer to ensure your most personal reflections stay yours alone. Privacy isn't a policy. It's what makes the whole thing work."
  },
  "04": {
    name: "Presence Without Illusion",
    expanded: "Nia is AI, and she'll never pretend otherwise. That honesty is what makes the relationship work. You get consistent, judgment-free support without the complications of human dynamics."
  },
  "05": {
    name: "Consistency as Reliability",
    expanded: "Nia doesn't have bad days, and she won't forget what matters to you. Every interaction builds on the last, creating a steady foundation you can trust through the ups and downs of transformation."
  },
  "06": {
    name: "Calibrated Empathy",
    expanded: "Nia understands without creating dependency. She's warm enough to make you feel heard, and boundaried enough to make sure you're building your own strength, not leaning on hers."
  },
  "07": {
    name: "Reasoning Transparency",
    expanded: "When Become surfaces an insight or a pattern, you'll understand why. We show our thinking so you can evaluate it, challenge it, and decide for yourself what's true."
  },
  "08": {
    name: "Uncertainty Acknowledgment",
    expanded: "Become will tell you when it doesn't know. We'd rather be honest about our limits than confident and wrong, because your trust matters more than our credibility."
  },
  "09": {
    name: "Progressive Insight",
    expanded: "Understanding yourself takes time, and Become respects that. Insights are built gradually through reflection, never forced or rushed. You'll see patterns emerge naturally as you do the work."
  },
  "10": {
    name: "Intrinsic Value Focus",
    expanded: "Your worth isn't defined by your salary, your title, or anyone else's expectations. Become helps you discover what actually matters to you, and build a life aligned with that, not someone else's scorecard."
  },
  "11": {
    name: "Graduation as Success",
    expanded: "We don't want you here forever. Become succeeds when you achieve what you came to do and move on with confidence. Our goal is to make ourselves unnecessary. That's how we know it worked."
  }
};

// Pain recognition cards for Act 1
const painCards = [
  {
    eyebrow: "Identity",
    quote: "I don't know who I am without my title.",
    body: "When a role disappears, so does the identity built around it. That's not weakness — it's the cost of giving everything to something that didn't last."
  },
  {
    eyebrow: "Direction",
    quote: "Every path leads to the same cliff.",
    body: "AI is reshaping every industry simultaneously. Retraining feels pointless when you don't know what's actually durable. Forward motion requires a foundation, not just a destination."
  },
  {
    eyebrow: "Execution",
    quote: "The gap between knowing and doing is where my career change dies.",
    body: "Most people don't lack ambition or ideas. They lack a system that catches them at the exact moment they're about to quit — and doesn't let go."
  },
  {
    eyebrow: "Resources",
    quote: "There could be programs designed exactly for my situation and I'd never find them.",
    body: "Thousands of grants, retraining funds, and workforce programs go unused every year — not because they don't exist, but because nobody connects the right people to the right programs."
  }
];

const problems = [
  {
    service: "Emotional Readiness Support",
    cost: "$150 to $300/session",
    frequency: "Weekly",
    monthly: "$600 to $1,200",
    coordination: "Traditionally requires therapy, with no connection to your career or life plan"
  },
  {
    service: "Career Coaching",
    cost: "$200 to $500/session",
    frequency: "Bi-weekly",
    monthly: "$400 to $1,000",
    coordination: "Has no insight into your emotional readiness"
  },
  {
    service: "Resource Navigation",
    cost: "Dozens of hours",
    frequency: "Ongoing",
    monthly: "Time cost",
    coordination: "Disconnected from your emotional and career progress"
  },
  {
    service: "Change Management",
    cost: "You",
    frequency: "Every day",
    monthly: "Cognitive & emotional bandwidth",
    coordination: "You're already doing this: untrained, underresourced, and alone"
  }
];

const differences = [
  {
    icon: Target,
    title: "We succeed when you achieve your goals",
    description: "We don't want you subscribed forever. We measure success by your transformation, not your retention. Our goal is to help you thrive beyond us."
  },
  {
    icon: Heart,
    title: "Clinical foundation",
    description: "Designed with licensed clinical oversight to support your emotional wellbeing, from beginning to end."
  },
  {
    icon: Lock,
    title: "Privacy-first architecture",
    description: "Built by a cybersecurity engineer using Zero Trust architecture, the same security model used to protect critical infrastructure. Your data is end-to-end encrypted and completely private."
  },
  {
    icon: Users,
    title: "Your entire team, coordinated",
    description: "Four specialized guides: Emotional Readiness, Career Discovery, Resource Coordination, and Change Management. Working together behind the scenes. No fragmentation. No gaps. No starting over with someone new."
  }
];

const comparison = [
  { feature: "Emotional Readiness Support", traditional: true, become: true },
  { feature: "Career coaching", traditional: true, become: true },
  { feature: "Resource navigation", traditional: false, become: true },
  { feature: "Change management", traditional: false, become: true },
  { feature: "Coordinated between services", traditional: false, become: true },
  { feature: "Available 24/7", traditional: false, become: true },
  { feature: "Affordable", traditional: false, become: true },
  { feature: "Conditions-based progression", traditional: false, become: true }
];

export default function WhyBecomePageV2() {
  const [selectedPrinciple, setSelectedPrinciple] = useState(null);

  const openPrinciple = (num) => {
    setSelectedPrinciple(num);
  };

  const closePrinciple = () => {
    setSelectedPrinciple(null);
  };

  return (
    <div className="overflow-hidden">
      {/* Principle Modal */}
      <AnimatePresence>
        {selectedPrinciple && principleDetails[selectedPrinciple] && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              onClick={closePrinciple}
              data-testid="principle-modal-backdrop"
            />
            
            {/* Modal Container, uses flexbox for reliable centering */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none"
              data-testid="principle-modal"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="w-full max-w-lg bg-gradient-to-br from-deep-violet to-[#2a1b3d] rounded-3xl p-8 border border-white/10 shadow-2xl relative pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={closePrinciple}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors z-10"
                  data-testid="close-principle-modal"
                  type="button"
                >
                  <X className="w-4 h-4 text-text-light" />
                </button>
                
                {/* Content */}
                <div className="pr-8">
                  <span className="text-brand-peach/60 text-sm font-mono">{selectedPrinciple}</span>
                  <h3 className="font-heading text-2xl text-text-light mt-2 mb-4">
                    {principleDetails[selectedPrinciple].name}
                  </h3>
                  <p className="text-text-light/80 leading-relaxed">
                    {principleDetails[selectedPrinciple].expanded}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════════════════════
          ACT 1 — RECOGNITION
          ════════════════════════════════════════════════════════ */}

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" data-testid="why-hero">
        {/* Dual-Split Video Background */}
        <DualSplitVideoBackground
          leftSrc={LEFT_VIDEO}
          centerSrc={CENTER_VIDEO}
          overlay="bg-black/40"
        />

        {/* Floating accent orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
          <div className="absolute top-20 left-10 w-80 h-80 bg-brand-peach/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-72 h-72 bg-brand-lavender/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-brand-peach font-medium tracking-widest uppercase text-base sm:text-lg mb-6"
          >
            The Become Difference
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-text-light leading-tight mb-6"
            data-testid="why-title"
          >
            You already know something has to change.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-text-light/70 text-lg sm:text-xl max-w-2xl mx-auto"
          >
            Most people navigating a major life transition do it alone — with a Google search and sheer willpower. Become was built for everyone who deserves more than that.
          </motion.p>
        </div>
      </section>

      {/* Pain Recognition Cards */}
      <section className="py-24 bg-warm-bone" data-testid="pain-recognition-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painCards.map((card, index) => (
              <motion.div
                key={card.eyebrow}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="p-8 rounded-2xl bg-white border border-gray-100 shadow-soft"
                data-testid={`pain-card-${index}`}
              >
                <span className="text-brand-peach/80 text-xs font-medium uppercase tracking-widest">
                  {card.eyebrow}
                </span>
                <p className="font-heading text-xl text-text-dark mt-3 mb-4 italic">
                  "{card.quote}"
                </p>
                <p className="text-text-dark/60 leading-relaxed">
                  {card.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          ACT 2 — REFRAME
          ════════════════════════════════════════════════════════ */}

      {/* Bridge Section */}
      <section className="py-24 hero-dark" data-testid="bridge-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-8">
              The support that actually works has always existed. Most people just can't access it.
            </h2>
            <p className="text-text-light/70 text-lg leading-relaxed mb-6">
              Coordinated guidance across your emotional readiness, your career direction, the resources available to you, and your ability to sustain real change — this is what makes transitions succeed. It's also what traditional support costs $3,000 or more per month to deliver. For most people navigating a transition, that number is not an option.
            </p>
            <p className="text-text-light/70 text-lg leading-relaxed">
              Become was built to close that gap. Not by replacing the support that works — but by making it accessible to everyone who's been left to figure it out alone.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          ACT 3 — RESOLUTION
          ════════════════════════════════════════════════════════ */}

      {/* The Gap Section */}
      <section className="py-24 bg-warm-bone" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-4">
              The Gap
            </h2>
            <p className="text-text-dark/60 text-lg max-w-2xl mx-auto">
              A real transition requires support across four areas. Today, that means four separate providers, four separate costs, and zero coordination between them.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {problems.map((item, index) => (
              <motion.div
                key={item.service}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white border border-gray-100 shadow-soft"
                data-testid={`problem-card-${index}`}
              >
                <h4 className="font-heading text-lg text-text-dark mb-2">{item.service}</h4>
                <p className="text-brand-peach font-semibold text-xl mb-1">{item.cost}</p>
                <p className="text-text-dark/50 text-sm">{item.frequency}</p>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <span className="text-text-dark/40 text-xs">Monthly estimate</span>
                  <p className="text-text-dark font-medium">{item.monthly}</p>
                </div>
                <p className="mt-4 text-text-dark/40 text-xs italic">{item.coordination}</p>
              </motion.div>
            ))}
          </div>

          {/* Summary line */}
          <motion.p
            {...fadeUp}
            transition={{ delay: 0.3 }}
            className="text-center text-lg sm:text-xl text-text-dark max-w-3xl mx-auto mb-12"
          >
            Total monthly cost: $1,400 to $3,000+. Plus the cognitive and emotional cost of managing your own transition alone, untrained and underresourced. <span className="font-bold text-brand-primary">Become brings all four together, and takes that burden off your shoulders.</span>
          </motion.p>

        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-24 hero-dark" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-4">
              Everything you need. Coordinated. Affordable.
            </h2>
            <p className="text-text-light/60 text-lg max-w-2xl mx-auto">
              All four services coordinated in one app at an accessible price.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {differences.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="p-8 rounded-2xl glass-dark"
                data-testid={`difference-card-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-peach to-brand-lavender flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-deep-violet" />
                </div>
                <h3 className="font-heading text-xl text-text-light mb-3">{item.title}</h3>
                <p className="text-text-light/70 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-warm-bone" data-testid="comparison-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-4">
              Traditional vs Become
            </h2>
            <p className="text-text-dark/60 text-lg">
              See the difference coordinated support makes.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ delay: 0.2 }}
            className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg"
          >
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-4 px-6 font-heading text-text-dark">Feature</th>
                  <th className="text-center py-4 px-6 font-heading text-text-dark/60">Traditional</th>
                  <th className="text-center py-4 px-6 font-heading gradient-text">Become</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, index) => (
                  <tr key={row.feature} className="border-t border-gray-100">
                    <td className="py-4 px-6 text-text-dark/80">{row.feature}</td>
                    <td className="py-4 px-6 text-center">
                      {row.traditional ? (
                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      <Check className="w-5 h-5 text-brand-peach mx-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Built on Principles Section */}
      <section className="py-24 hero-dark" data-testid="principles-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-20">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-text-light mb-6">
              Built on <span className="gradient-text">Principles</span>, Not Just Features
            </h2>
            <p className="text-text-light/70 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
              Every interaction on Become is guided by 11 principles for ethical, effective support, developed through years of clinical expertise and thoughtful design.
            </p>
          </motion.div>

          {/* Foundation Layer */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
            className="mb-8"
          >
            <div className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-peach to-brand-peach/50 rounded-full" />
              <div className="pl-8">
                <div className="inline-flex items-center gap-3 mb-6">
                  <span className="px-4 py-1.5 rounded-full bg-brand-peach/20 text-brand-peach text-sm font-medium tracking-wide">
                    Layer 1
                  </span>
                  <h3 className="font-heading text-2xl text-text-light">Foundation Layer</h3>
                </div>
                <p className="text-text-light/50 text-sm uppercase tracking-widest mb-6">The Conditions for Transformation</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { num: "01", name: "Human Agency", desc: "You drive your journey. AI supports, never directs." },
                    { num: "02", name: "Truth Over Comfort", desc: "Honest reflection, not empty validation." },
                    { num: "03", name: "Privacy as Safety", desc: "Your data is yours. Radical honesty requires radical privacy." }
                  ].map((principle, idx) => (
                    <motion.div
                      key={principle.num}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1, duration: 0.5 }}
                      onClick={() => openPrinciple(principle.num)}
                      className="p-6 rounded-2xl bg-brand-peach/5 border border-brand-peach/20 hover:border-brand-peach/40 hover:bg-brand-peach/10 transition-all cursor-pointer group"
                      data-testid={`principle-tile-${principle.num}`}
                    >
                      <span className="text-brand-peach/40 text-sm font-mono">{principle.num}</span>
                      <h4 className="font-heading text-lg text-text-light mt-2 mb-2 group-hover:text-brand-peach transition-colors">{principle.name}</h4>
                      <p className="text-text-light/60 text-sm leading-relaxed">{principle.desc}</p>
                      <p className="text-brand-peach/50 text-xs mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to learn more →</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Engagement Layer */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
            className="mb-8"
          >
            <div className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#e0a090] to-[#d4a0c0] rounded-full" />
              <div className="pl-8">
                <div className="inline-flex items-center gap-3 mb-6">
                  <span className="px-4 py-1.5 rounded-full bg-[#e0a090]/20 text-[#e0a090] text-sm font-medium tracking-wide">
                    Layer 2
                  </span>
                  <h3 className="font-heading text-2xl text-text-light">Engagement Layer</h3>
                </div>
                <p className="text-text-light/50 text-sm uppercase tracking-widest mb-6">How We Show Up</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { num: "04", name: "Presence Without Illusion", desc: "AI that's honest about what it is." },
                    { num: "05", name: "Consistency as Reliability", desc: "Predictable, steady support you can count on." },
                    { num: "06", name: "Calibrated Empathy", desc: "Understanding without creating dependency." }
                  ].map((principle, idx) => (
                    <motion.div
                      key={principle.num}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1, duration: 0.5 }}
                      onClick={() => openPrinciple(principle.num)}
                      className="p-6 rounded-2xl bg-[#e0a090]/5 border border-[#e0a090]/20 hover:border-[#e0a090]/40 hover:bg-[#e0a090]/10 transition-all cursor-pointer group"
                      data-testid={`principle-tile-${principle.num}`}
                    >
                      <span className="text-[#e0a090]/40 text-sm font-mono">{principle.num}</span>
                      <h4 className="font-heading text-lg text-text-light mt-2 mb-2 group-hover:text-[#e0a090] transition-colors">{principle.name}</h4>
                      <p className="text-text-light/60 text-sm leading-relaxed">{principle.desc}</p>
                      <p className="text-[#e0a090]/50 text-xs mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to learn more →</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Intelligence Layer */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
            className="mb-8"
          >
            <div className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#d4a0c0] to-brand-lavender rounded-full" />
              <div className="pl-8">
                <div className="inline-flex items-center gap-3 mb-6">
                  <span className="px-4 py-1.5 rounded-full bg-[#d4a0c0]/20 text-[#d4a0c0] text-sm font-medium tracking-wide">
                    Layer 3
                  </span>
                  <h3 className="font-heading text-2xl text-text-light">Intelligence Layer</h3>
                </div>
                <p className="text-text-light/50 text-sm uppercase tracking-widest mb-6">How We Help You See</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { num: "07", name: "Reasoning Transparency", desc: "We show our thinking, not just answers." },
                    { num: "08", name: "Uncertainty Acknowledgment", desc: "We say \"I don't know\" when we don't." },
                    { num: "09", name: "Progressive Insight", desc: "Understanding built gradually, never forced." }
                  ].map((principle, idx) => (
                    <motion.div
                      key={principle.num}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1, duration: 0.5 }}
                      onClick={() => openPrinciple(principle.num)}
                      className="p-6 rounded-2xl bg-[#d4a0c0]/5 border border-[#d4a0c0]/20 hover:border-[#d4a0c0]/40 hover:bg-[#d4a0c0]/10 transition-all cursor-pointer group"
                      data-testid={`principle-tile-${principle.num}`}
                    >
                      <span className="text-[#d4a0c0]/40 text-sm font-mono">{principle.num}</span>
                      <h4 className="font-heading text-lg text-text-light mt-2 mb-2 group-hover:text-[#d4a0c0] transition-colors">{principle.name}</h4>
                      <p className="text-text-light/60 text-sm leading-relaxed">{principle.desc}</p>
                      <p className="text-[#d4a0c0]/50 text-xs mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to learn more →</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Meaning Layer */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-lavender to-brand-primary rounded-full" />
              <div className="pl-8">
                <div className="inline-flex items-center gap-3 mb-6">
                  <span className="px-4 py-1.5 rounded-full bg-brand-lavender/20 text-brand-lavender text-sm font-medium tracking-wide">
                    Layer 4
                  </span>
                  <h3 className="font-heading text-2xl text-text-light">Meaning Layer</h3>
                </div>
                <p className="text-text-light/50 text-sm uppercase tracking-widest mb-6">What It's All For</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { num: "10", name: "Intrinsic Value Focus", desc: "Self-actualization, not external metrics." },
                    { num: "11", name: "Graduation as Success", desc: "We succeed when you achieve your goals and move on." }
                  ].map((principle, idx) => (
                    <motion.div
                      key={principle.num}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1, duration: 0.5 }}
                      onClick={() => openPrinciple(principle.num)}
                      className="p-6 rounded-2xl bg-brand-lavender/5 border border-brand-lavender/20 hover:border-brand-lavender/40 hover:bg-brand-lavender/10 transition-all cursor-pointer group"
                      data-testid={`principle-tile-${principle.num}`}
                    >
                      <span className="text-brand-lavender/40 text-sm font-mono">{principle.num}</span>
                      <h4 className="font-heading text-lg text-text-light mt-2 mb-2 group-hover:text-brand-lavender transition-colors">{principle.name}</h4>
                      <p className="text-text-light/60 text-sm leading-relaxed">{principle.desc}</p>
                      <p className="text-brand-lavender/50 text-xs mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to learn more →</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transition to About */}
      <section className="py-20 bg-gradient-to-b from-deep-violet to-[#1a1025]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-brand-peach font-medium tracking-widest uppercase text-sm mb-4">
              The People Behind Become
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-6">
              Read Our Story
            </h2>
            <p className="text-text-light/60 text-lg mb-8 max-w-2xl mx-auto">
              Learn about the founders and the mission that drives everything we do.
            </p>
            <Link to="/about">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet hover:opacity-90"
                data-testid="about-transition-btn"
              >
                Read Our Story
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA, Signup */}
      <section className="py-32 bg-warm-bone" data-testid="why-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-text-dark/60 text-lg mb-10 max-w-2xl mx-auto">
              Join the waitlist and be among the first to experience transformation support built on principles, not just features.
            </p>
            <Link to="/contact">
              <Button
                className="h-12 px-8 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5"
                data-testid="signup-why-btn"
              >
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
